import { useQueryClient } from '@tanstack/react-query';

import type { Notification } from '../../model/types';
import { notificationQueryKeys } from '../query-keys';
import { useNotificationUpdate } from './use-notification-update';

interface OptimisticUpdateContext {
  previousNotifications?: Notification[];
}

export const useNotificationMarkAsRead = () => {
  const queryClient = useQueryClient();

  const [updateNotification, ...rest] = useNotificationUpdate<OptimisticUpdateContext>({
    onMutate: async ({ notificationIds }): Promise<OptimisticUpdateContext> => {
      const queryKey = notificationQueryKeys.lists();

      await queryClient.cancelQueries({ queryKey: queryKey });

      const previousNotifications = queryClient.getQueryData<Notification[]>(queryKey);

      if (previousNotifications) {
        const optimisticNotifications = previousNotifications.map(notification =>
          notificationIds.includes(notification.id)
            ? {
                ...notification,
                attributes: {
                  ...notification.attributes,
                  isRead: true
                }
              }
            : notification
        );

        queryClient.setQueryData(queryKey, optimisticNotifications);
      }

      return { previousNotifications };
    },

    onError: (_error, _variables, context) => {
      const queryKey = notificationQueryKeys.lists();
      if (context?.previousNotifications) {
        queryClient.setQueryData(queryKey, context.previousNotifications);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists() });
    }
  });

  const markAsRead = async (notificationIds: string[]) => {
    await updateNotification({ notificationIds, isRead: true });
  };

  return [markAsRead, rest] as const;
};
